import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

// Short-lived cache shared by the cart page and drawer. No customer address is stored.
const estimates = new Map();
const destinations = {
  SE: { country: 'Sweden', zip: '11453', city: 'Stockholm' },
  FI: { country: 'Finland', zip: '00100', city: 'Helsinki' },
  DK: { country: 'Denmark', zip: '1050', city: 'Copenhagen' },
};
const warehousePickup = /upphämtning|hämtas|hämta\s+(i|på|hos)|afhentning|nouto\s+(varastolta|myymälästä)|pick[ -]?up\s+(at|from)|collect\s+in\s+store|click\s*&\s*collect/i;

const CREATE_ESTIMATE_CART = `mutation EstimateCart($input: CartInput!, $country: CountryCode!) @inContext(country: $country) {
  cartCreate(input: $input) { cart { id totalQuantity cost { subtotalAmount { amount currencyCode } } } userErrors { message } }
}`;
const ESTIMATE_RATES = `query EstimateRates($id: ID!, $country: CountryCode!) @inContext(country: $country) {
  cart(id: $id) { ... @defer { deliveryGroups(first: 25, withCarrierRates: true) {
    pageInfo { hasNextPage }
    edges { node { deliveryOptions { title deliveryMethodType estimatedCost { amount currencyCode } } } }
  } } }
}`;

function pickShipping(groups, currency) {
  if (!groups?.edges?.length || groups.pageInfo?.hasNextPage) throw new Error('Incomplete delivery groups');
  let price = 0;
  for (const { node } of groups.edges) {
    const rates = (node?.deliveryOptions || []).filter(rate => {
      const amount = Number(rate.estimatedCost?.amount);
      return ['SHIPPING', 'PICKUP_POINT', 'LOCAL'].includes(rate.deliveryMethodType)
        && !warehousePickup.test(rate.title || '')
        && rate.estimatedCost?.currencyCode === currency
        && Number.isFinite(amount) && amount >= 0;
    });
    if (!rates.length) throw new Error('Missing delivery group rate');
    price += Math.min(...rates.map(rate => Number(rate.estimatedCost.amount)));
  }
  return { price, currency };
}

function graphqlParts(text, contentType) {
  if (!contentType.includes('multipart/')) return [JSON.parse(text)];
  const boundary = contentType.match(/boundary=(?:"([^"]+)"|([^;\s]+))/i);
  if (!boundary) throw new Error('Missing multipart boundary');
  return text.split('--' + (boundary[1] || boundary[2])).flatMap(part => {
    const start = part.indexOf('{');
    if (start < 0) return [];
    return [JSON.parse(part.slice(start).trim())];
  });
}

async function getEstimate(cart, country) {
  // A separate tokenless Storefront cart is used solely for the estimate.
  // Never call Ajax shipping_rates: that endpoint can prefill the real checkout.
  const input = {
    buyerIdentity: { countryCode: country },
    delivery: { addresses: [{ selected: true, oneTimeUse: true, address: { deliveryAddress: {
      countryCode: country, zip: destinations[country].zip, city: destinations[country].city, address1: '-',
    } } }] },
    discountCodes: (cart.discount_codes || []).filter(code => code.applicable !== false).map(code => code.code),
    attributes: Object.entries(cart.attributes || {}).map(([key, value]) => ({ key, value: String(value) })),
    lines: cart.items.map(item => ({
      merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
      quantity: item.quantity,
      attributes: Object.entries(item.properties || {}).map(([key, value]) => ({ key, value: String(value) })),
      ...(item.selling_plan_allocation ? { sellingPlanId: `gid://shopify/SellingPlan/${item.selling_plan_allocation.selling_plan.id}` } : {}),
    })),
  };
  const key = JSON.stringify([country, cart.currency, cart.total_price, cart.items_subtotal_price, input]);
  const cached = estimates.get(key);
  if (cached && cached.expires > Date.now()) return cached.promise;
  const promise = (async () => {
    const signal = AbortSignal.timeout(25000);
    const request = (query, variables, accept = 'application/json') => fetch('/api/2026-10/graphql.json', {
      method: 'POST', credentials: 'omit', signal,
      headers: { 'Content-Type': 'application/json', Accept: accept },
      body: JSON.stringify({ query, variables }),
    });
    const created = await request(CREATE_ESTIMATE_CART, { input, country });
    if (!created.ok) throw new Error('Estimate cart unavailable');
    const data = await created.json();
    const clone = data.data?.cartCreate?.cart;
    if (data.errors?.length || data.data?.cartCreate?.userErrors?.length || !clone?.id || clone.totalQuantity !== cart.item_count) throw new Error('Estimate cart mismatch');
    const subtotal = clone.cost?.subtotalAmount;
    if (subtotal?.currencyCode !== cart.currency || Math.abs(Number(subtotal.amount) * 100 - cart.items_subtotal_price) > 2) throw new Error('Estimate pricing mismatch');
    const response = await request(ESTIMATE_RATES, { id: clone.id, country }, 'multipart/mixed; deferSpec=20220824, application/json');
    if (!response.ok) throw new Error('Shipping unavailable');
    const parts = graphqlParts(await response.text(), response.headers.get('content-type') || '');
    let groups;
    for (const part of parts) {
      if (part.errors?.length) throw new Error('Shipping query failed');
      if (part.data?.cart?.deliveryGroups) groups = part.data.cart.deliveryGroups;
      for (const incremental of part.incremental || []) {
        if (incremental.errors?.length) throw new Error('Shipping query failed');
        if (incremental.data?.deliveryGroups) groups = incremental.data.deliveryGroups;
      }
    }
    return pickShipping(groups, cart.currency);
  })();
  estimates.clear();
  estimates.set(key, { promise, expires: Date.now() + 120000 });
  try { return await promise; } catch (error) { estimates.delete(key); throw error; }
}

function formatCartMoney(cents, template) {
  // Match the existing Liquid format-price output, including currency placement.
  const sample = template.match(/\d[\d\s\u00a0\u202f.,']*\d/);
  if (!sample || !Number.isSafeInteger(cents)) throw new Error('Invalid money format');
  const decimals = /[,.]\d{2}$/.test(sample[0]) ? 2 : 0;
  const number = new Intl.NumberFormat('sv-SE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(cents / 100).replace(/[\u00a0\u202f]/g, ' ');
  return template.replace(sample[0], number);
}

class LineupShippingEstimator extends Component {
  #listeners;
  #request;
  #timer;
  #version = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners = new AbortController();
    document.addEventListener(ThemeEvents.cartUpdate, this.#schedule, { signal: this.#listeners.signal });
    this.#schedule();
  }
  updatedCallback() {
    super.updatedCallback();
    this.#schedule();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#request?.abort();
    clearTimeout(this.#timer);
    this.#version++;
  }
  #schedule = () => {
    this.#version++;
    this.#request?.abort();
    clearTimeout(this.#timer);
    this.refs.amount.textContent = this.dataset.loading;
    this.refs.total.textContent = '—';
    this.setAttribute('aria-busy', 'true');
    this.refs.note.hidden = true;
    this.#timer = setTimeout(() => this.#refresh(), 250);
  };
  async #refresh() {
    const version = this.#version;
    const country = this.dataset.country;
    const root = window.Shopify?.routes?.root || '/';
    const current = () => version === this.#version && this.isConnected;
    try {
      if (!destinations[country]) throw new Error('Unsupported market');
      this.#request = new AbortController();
      const response = await fetch(`${root}cart.js`, { credentials: 'same-origin', signal: this.#request.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart unavailable');
      const cart = await response.json();
      if (!current()) return;
      if (!cart.item_count || !cart.requires_shipping) { this.hidden = true; return; }
      this.hidden = false;
      const estimate = await getEstimate(cart, country);
      if (!current()) return;
      const shippingCents = Math.round(estimate.price * 100);
      this.refs.amount.textContent = formatCartMoney(shippingCents, this.dataset.moneyTemplate);
      this.refs.total.textContent = formatCartMoney(cart.total_price + shippingCents, this.dataset.moneyTemplate);
      this.removeAttribute('aria-busy');
      this.refs.note.hidden = false;
    } catch (error) {
      if (current()) { this.refs.amount.textContent = this.dataset.fallback; this.refs.total.textContent = '—'; this.refs.note.hidden = true; this.removeAttribute('aria-busy'); }
    }
  }
}
if (!customElements.get('lineup-shipping-estimator')) customElements.define('lineup-shipping-estimator', LineupShippingEstimator);
